import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CreditCard, ArrowLeft } from 'lucide-react';
import StoreLayout from '../../components/store/StoreLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { useCart } from '../../contexts/CartContext';
import { checkoutAPI, paymentAPI } from '../../lib/api';
import { formatPrice } from '../../lib/utils';
import { trackInitiateCheckout, trackAddPaymentInfo } from '../../lib/tracking';
import { toast } from 'sonner';

export default function CheckoutPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cart, clearCart, applyPromo } = useCart();

  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('viva');
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'FR',
    phone: '',
  });

  React.useEffect(() => {
    if (cart) {
      trackInitiateCheckout(cart);
    }
  }, [cart]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // Appliquer un code promo
  const handleApplyPromo = async (e) => {
    e.preventDefault();
    if (!promoCode.trim()) return;

    setPromoLoading(true);
    try {
      await applyPromo(promoCode);
      toast.success('Code promo appliqué !');
      setPromoCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code promo invalide');
    } finally {
      setPromoLoading(false);
    }
  };

  // Retirer le code promo
  const handleRemovePromo = async () => {
    setPromoLoading(true);
    try {
      await applyPromo(''); // convention backend: string vide retire le code
      toast.success('Code promo retiré.');
    } catch (error) {
      toast.error('Impossible de retirer le code promo.');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cart || cart.items.length === 0) {
      toast.error('Votre panier est vide');
      return;
    }

    setLoading(true);
    try {
      // Create order
      const orderResponse = await checkoutAPI.createOrder({
        cart_id: cart.id,
        email: formData.email,
        shipping_address: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          address_line1: formData.address,
          city: formData.city,
          postal_code: formData.postalCode,
          country: formData.country,
          phone: formData.phone,
        }
      });

      const orderId = orderResponse.data.id;
      trackAddPaymentInfo(cart.total_cents);

      // Create payment
      let paymentResponse;
      if (paymentMethod === 'viva') {
        paymentResponse = await paymentAPI.createVivaPayment(orderId);
      } else {
        paymentResponse = await paymentAPI.createPayPalPayment(orderId);
      }

      // For demo: simulate successful payment
      await paymentAPI.mockSuccess(orderId);

      // Clear cart and redirect
      clearCart();
      navigate(`/order/${orderId}`);

    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Erreur lors du paiement. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (!cart || cart.items.length === 0) {
    return (
      <StoreLayout>
        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <p className="text-muted-foreground mb-4">Votre panier est vide</p>
          <Button onClick={() => navigate('/looks')}>Voir les looks</Button>
        </div>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <Button
            variant="ghost"
            className="mb-6"
            onClick={() => navigate('/cart')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour au panier
          </Button>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-4xl font-bold mb-8"
          >
            {t('checkout.title')}
          </motion.h1>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Form */}
              <div className="lg:col-span-2 space-y-8">
                {/* Contact */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-border rounded-lg p-6"
                >
                  <h2 className="font-semibold text-lg mb-4">Contact</h2>
                  <div>
                    <Label htmlFor="email">{t('form.email')} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      data-testid="checkout-email"
                    />
                  </div>
                </motion.div>

                {/* Shipping */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="border border-border rounded-lg p-6"
                >
                  <h2 className="font-semibold text-lg mb-4">{t('checkout.shipping')}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">{t('form.firstName')} *</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="checkout-firstname"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">{t('form.lastName')} *</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="checkout-lastname"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="address">{t('form.address')} *</Label>
                      <Input
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="checkout-address"
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">{t('form.city')} *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="checkout-city"
                      />
                    </div>
                    <div>
                      <Label htmlFor="postalCode">{t('form.postalCode')} *</Label>
                      <Input
                        id="postalCode"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="checkout-postal"
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">{t('form.country')} *</Label>
                      <Input
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">{t('form.phone')}</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </motion.div>

                {/* Payment */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="border border-border rounded-lg p-6"
                >
                  <h2 className="font-semibold text-lg mb-4">{t('checkout.payment')}</h2>
                  <RadioGroup
                    value={paymentMethod}
                    onValueChange={setPaymentMethod}
                    className="space-y-3"
                  >
                    <div className="flex items-center space-x-3 border border-border rounded-lg p-4 cursor-pointer hover:bg-muted/50">
                      <RadioGroupItem value="viva" id="viva" />
                      <Label htmlFor="viva" className="flex items-center gap-3 cursor-pointer flex-1">
                        <CreditCard className="h-5 w-5" />
                        {t('checkout.payment.viva')}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 border border-border rounded-lg p-4 cursor-pointer hover:bg-muted/50">
                      <RadioGroupItem value="paypal" id="paypal" />
                      <Label htmlFor="paypal" className="flex items-center gap-3 cursor-pointer flex-1">
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106z" />
                        </svg>
                        {t('checkout.payment.paypal')}
                      </Label>
                    </div>
                  </RadioGroup>

                  <p className="text-sm text-muted-foreground mt-4">
                    Mode démo : le paiement sera simulé automatiquement.
                  </p>
                </motion.div>
              </div>

              {/* Summary */}
              <div className="lg:col-span-1">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="border border-border rounded-lg p-6 sticky top-24"
                >
                  <h3 className="font-semibold text-lg mb-4">Votre commande</h3>

                  {/* Items preview */}
                  <div className="space-y-3 mb-6">
                    {cart.items.map((item) => (
                      <div key={item.id} className="flex gap-3">
                        <div className="w-14 h-16 bg-muted overflow-hidden flex-shrink-0">
                          <img
                            src={item.look?.images?.[0] || item.product?.images?.[0] || ''}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {item.look?.name || item.product?.name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Qté: {item.quantity}
                          </p>
                        </div>
                        <p className="text-sm font-medium">
                          {formatPrice(item.price_cents * item.quantity)}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Promo code */}
                  {!cart.promo_code ? (
                    <form className="flex gap-2 mb-6" onSubmit={handleApplyPromo}>
                      <Input
                        placeholder="Code promo"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value)}
                        className="bg-background"
                        disabled={promoLoading}
                      />
                      <Button
                        type="submit"
                        variant="outline"
                        disabled={promoLoading || !promoCode.trim()}
                      >
                        {promoLoading ? '...' : 'Appliquer'}
                      </Button>
                    </form>
                  ) : (
                    <div className="flex justify-between items-center bg-green-50 text-green-700 px-3 py-2 rounded-md text-sm mb-6">
                      <span className="font-medium">Code appliqué : {cart.promo_code}</span>
                      <button
                        type="button"
                        onClick={handleRemovePromo}
                        className="text-xs underline"
                        disabled={promoLoading}
                      >
                        {promoLoading ? 'Retrait...' : 'Retirer'}
                      </button>
                    </div>
                  )}

                  <div className="space-y-2 text-sm border-t border-border pt-4">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('cart.subtotal')}</span>
                      <span>{formatPrice(cart.subtotal_cents)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('cart.shipping')}</span>
                      <span>{cart.shipping_cents === 0 ? 'Gratuite' : formatPrice(cart.shipping_cents)}</span>
                    </div>
                    {cart.discount_cents > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>{t('cart.discount')}</span>
                        <span>-{formatPrice(cart.discount_cents)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-semibold pt-2 border-t border-border">
                      <span>{t('cart.total')}</span>
                      <span>{formatPrice(cart.total_cents)}</span>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full mt-6"
                    disabled={loading}
                    data-testid="place-order-btn"
                  >
                    {loading ? 'Traitement...' : t('checkout.order')}
                  </Button>
                </motion.div>
              </div>
            </div>
          </form>
        </div>
      </section>
    </StoreLayout>
  );
}
